import React, { useState, useEffect } from 'react';
import apiClient from '../../services/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  DollarSign,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  RefreshCw,
  Search,
  Ban,
  Unlock,
  RotateCcw,
  Eye,
  FileText,
  User,
  Calendar,
  CreditCard,
  Filter,
} from 'lucide-react';

const AdminPayoutManagement = () => {
  const [payouts, setPayouts] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Action modals
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showFailModal, setShowFailModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showFreezeModal, setShowFreezeModal] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [actionReason, setActionReason] = useState('');
  const [payoutReference, setPayoutReference] = useState('');
  
  const [executingPayouts, setExecutingPayouts] = useState(false);
  
  // Audit log modal
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  useEffect(() => {
    fetchPayouts();
  }, [statusFilter]);

  const fetchPayouts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status_filter', statusFilter);
      params.append('limit', '100');
      
      const response = await apiClient.get(`/wallet/admin/payouts?${params.toString()}`);
      setPayouts(response.data.payouts || []);
      setStats(response.data.stats || {});
    } catch (err) {
      console.error('Failed to fetch payouts:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLog = async (payoutId = null) => {
    setAuditLoading(true);
    try {
      const url = payoutId 
        ? `/wallet/admin/payouts/audit-log?payout_id=${payoutId}`
        : '/wallet/admin/payouts/audit-log?limit=100';
      const response = await apiClient.get(url);
      setAuditLogs(response.data.audit_logs || []);
    } catch (err) {
      console.error('Failed to fetch audit log:', err);
    } finally {
      setAuditLoading(false);
    }
  };

  const handleApprove = async (payoutId) => {
    setActionLoading(prev => ({ ...prev, [payoutId]: true }));
    try {
      await apiClient.post(`/wallet/admin/payouts/${payoutId}/approve`, {});
      fetchPayouts();
    } catch (err) {
      alert('Failed to approve: ' + (err.response?.data?.detail || 'Unknown error'));
    } finally {
      setActionLoading(prev => ({ ...prev, [payoutId]: false }));
    }
  };

  const handleReject = async () => {
    if (!actionReason.trim()) {
      alert('Rejection reason is required');
      return;
    }
    setActionLoading(prev => ({ ...prev, [selectedPayout.id]: true }));
    try {
      await apiClient.post(`/wallet/admin/payouts/${selectedPayout.id}/reject`, {
        reason: actionReason
      });
      setShowRejectModal(false);
      setActionReason('');
      setSelectedPayout(null);
      fetchPayouts();
    } catch (err) {
      alert('Failed to reject: ' + (err.response?.data?.detail || 'Unknown error'));
    } finally {
      setActionLoading(prev => ({ ...prev, [selectedPayout?.id]: false }));
    }
  };

  const handleComplete = async () => {
    setActionLoading(prev => ({ ...prev, [selectedPayout.id]: true }));
    try {
      await apiClient.post(`/wallet/admin/payouts/${selectedPayout.id}/complete`, {
        payout_reference: payoutReference || undefined
      });
      setShowCompleteModal(false);
      setPayoutReference('');
      setSelectedPayout(null);
      fetchPayouts();
    } catch (err) {
      alert('Failed to complete: ' + (err.response?.data?.detail || 'Unknown error'));
    } finally {
      setActionLoading(prev => ({ ...prev, [selectedPayout?.id]: false }));
    }
  };

  const handleFail = async () => {
    if (!actionReason.trim()) {
      alert('Failure reason is required');
      return;
    }
    setActionLoading(prev => ({ ...prev, [selectedPayout.id]: true }));
    try {
      await apiClient.post(`/wallet/admin/payouts/${selectedPayout.id}/fail`, {
        reason: actionReason
      });
      setShowFailModal(false);
      setActionReason('');
      setSelectedPayout(null);
      fetchPayouts();
    } catch (err) {
      alert('Failed to mark as failed: ' + (err.response?.data?.detail || 'Unknown error'));
    } finally {
      setActionLoading(prev => ({ ...prev, [selectedPayout?.id]: false }));
    }
  };

  const handleRetry = async (payoutId) => {
    setActionLoading(prev => ({ ...prev, [payoutId]: true }));
    try {
      await apiClient.post(`/wallet/admin/payouts/${payoutId}/retry`, {});
      fetchPayouts();
    } catch (err) {
      alert('Failed to retry: ' + (err.response?.data?.detail || 'Unknown error'));
    } finally {
      setActionLoading(prev => ({ ...prev, [payoutId]: false }));
    }
  };

  const handleFreeze = async (userId, reason) => {
    try {
      await apiClient.post(`/wallet/admin/users/${userId}/freeze-payouts`, {
        reason: reason || 'Frozen by admin'
      });
      fetchPayouts();
      alert('User payouts frozen');
    } catch (err) {
      alert('Failed to freeze: ' + (err.response?.data?.detail || 'Unknown error'));
    }
  };

  const handleUnfreeze = async (userId) => {
    try {
      await apiClient.post(`/wallet/admin/users/${userId}/unfreeze-payouts`, {});
      fetchPayouts();
      alert('User payouts unfrozen');
    } catch (err) {
      alert('Failed to unfreeze: ' + (err.response?.data?.detail || 'Unknown error'));
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      processing: 'bg-blue-500/20 text-blue-400',
      completed: 'bg-emerald-500/20 text-emerald-400',
      failed: 'bg-red-500/20 text-red-400',
      rejected: 'bg-red-500/20 text-red-400'
    };
    return <Badge className={styles[status] || 'bg-slate-500/20 text-muted-foreground'}>{status}</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleExecuteAutomatedPayouts = async () => {
    if (!window.confirm('Execute automated referral payouts for ALL eligible users now? This will trigger Stripe transfers for any users who are payout-eligible.')) {
      return;
    }
    setExecutingPayouts(true);
    try {
      const response = await apiClient.post('/payouts/execute', {
        force: false,
        superadmin_override: false,
      });
      const data = response.data || {};
      const summary = `Processed: ${data.processed || 0} | Successful: ${data.successful || 0} | Failed: ${data.failed || 0} | Skipped: ${data.skipped || 0}`;
      if ((data.successful || 0) > 0) {
        toast.success(`Automated payouts executed. ${summary}`);
      } else if ((data.processed || 0) === 0) {
        toast.info('No eligible users for automated payout right now.');
      } else {
        toast.warning(`Payout batch finished. ${summary}`);
      }
      fetchPayouts();
    } catch (err) {
      toast.error('Failed to execute automated payouts: ' + (err.response?.data?.detail || err.message));
    } finally {
      setExecutingPayouts(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const filteredPayouts = payouts.filter(p => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      p.creator_name?.toLowerCase().includes(query) ||
      p.creator_email?.toLowerCase().includes(query) ||
      p.id?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-yellow-500/10 border-yellow-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-400 text-xs">Pending</p>
                <p className="text-2xl font-bold text-foreground">{stats.pending || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-blue-500/10 border-blue-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-400 text-xs">Processing</p>
                <p className="text-2xl font-bold text-foreground">{stats.processing || 0}</p>
              </div>
              <Loader2 className="w-8 h-8 text-blue-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-emerald-500/10 border-emerald-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-400 text-xs">Completed</p>
                <p className="text-2xl font-bold text-foreground">{stats.completed || 0}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-emerald-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-red-500/10 border-red-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-400 text-xs">Failed</p>
                <p className="text-2xl font-bold text-foreground">{stats.failed || 0}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-500/10 border-border/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-xs">Rejected</p>
                <p className="text-2xl font-bold text-foreground">{stats.rejected || 0}</p>
              </div>
              <XCircle className="w-8 h-8 text-muted-foreground opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters & Actions */}
      <Card className="bg-card/50 border-border">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40 bg-secondary border-border">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-secondary border-border"
              />
            </div>
            
            <Button
              onClick={handleExecuteAutomatedPayouts}
              disabled={executingPayouts}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="execute-automated-payouts-btn"
            >
              {executingPayouts ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <DollarSign className="w-4 h-4 mr-2" />
              )}
              Execute Automated Payouts
            </Button>
            
            <Button
              onClick={() => { setShowAuditModal(true); fetchAuditLog(); }}
              variant="outline"
              className="border-border"
            >
              <FileText className="w-4 h-4 mr-2" /> View Audit Log
            </Button>
            
            <Button onClick={fetchPayouts} variant="outline" className="border-border">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Payouts Table */}
      <Card className="bg-card/50 border-border">
        <CardHeader>
          <CardTitle className="text-foreground flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-400" />
            Payout Requests ({filteredPayouts.length})
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Manage creator withdrawal requests. This is a manual payout flow - mark as completed after bank transfer.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
            </div>
          ) : filteredPayouts.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-muted-foreground">No payout requests found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredPayouts.map((payout) => (
                <div
                  key={payout.id}
                  className="p-4 bg-secondary/50 rounded-xl border border-border hover:border-border transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    {/* Payout Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                          <User className="w-5 h-5 text-emerald-400" />
                        </div>
                        <div>
                          <h4 className="text-foreground font-medium flex items-center gap-2">
                            {payout.creator_name || 'Unknown'}
                            {payout.payout_frozen && (
                              <Badge className="bg-red-500/20 text-red-400 text-xs">FROZEN</Badge>
                            )}
                          </h4>
                          <p className="text-muted-foreground text-sm">{payout.creator_email}</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground text-xs">Amount</p>
                          <p className="text-emerald-400 font-bold text-lg">{formatCurrency(payout.amount)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-xs">Status</p>
                          <div className="mt-1">{getStatusBadge(payout.status)}</div>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-xs">Bank Account</p>
                          <p className="text-foreground flex items-center gap-1">
                            <CreditCard className="w-3 h-3 text-muted-foreground" />
                            {payout.bank_name || 'N/A'} ****{payout.account_last4 || '????'}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-xs">Requested</p>
                          <p className="text-foreground/80 flex items-center gap-1">
                            <Calendar className="w-3 h-3 text-muted-foreground" />
                            {formatDate(payout.created_at)}
                          </p>
                        </div>
                      </div>
                      
                      {/* Additional info for processed payouts */}
                      {(payout.failure_reason || payout.rejection_reason || payout.payout_reference) && (
                        <div className="mt-3 p-2 bg-card rounded-lg text-sm">
                          {payout.failure_reason && (
                            <p className="text-red-400"><strong>Failure Reason:</strong> {payout.failure_reason}</p>
                          )}
                          {payout.rejection_reason && (
                            <p className="text-red-400"><strong>Rejection Reason:</strong> {payout.rejection_reason}</p>
                          )}
                          {payout.payout_reference && (
                            <p className="text-emerald-400"><strong>Reference:</strong> {payout.payout_reference}</p>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                      {payout.status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleApprove(payout.id)}
                            disabled={actionLoading[payout.id] || payout.payout_frozen}
                            className="bg-emerald-500 hover:bg-emerald-600"
                          >
                            {actionLoading[payout.id] ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <><CheckCircle2 className="w-4 h-4 mr-1" /> Approve</>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => { setSelectedPayout(payout); setShowRejectModal(true); }}
                            disabled={actionLoading[payout.id]}
                          >
                            <XCircle className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </>
                      )}
                      
                      {payout.status === 'processing' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => { setSelectedPayout(payout); setShowCompleteModal(true); }}
                            disabled={actionLoading[payout.id]}
                            className="bg-emerald-500 hover:bg-emerald-600"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" /> Complete
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => { setSelectedPayout(payout); setShowFailModal(true); }}
                            disabled={actionLoading[payout.id]}
                          >
                            <AlertCircle className="w-4 h-4 mr-1" /> Mark Failed
                          </Button>
                        </>
                      )}
                      
                      {payout.status === 'failed' && (
                        <Button
                          size="sm"
                          onClick={() => handleRetry(payout.id)}
                          disabled={actionLoading[payout.id] || payout.payout_frozen}
                          className="bg-blue-500 hover:bg-blue-600"
                        >
                          {actionLoading[payout.id] ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <><RotateCcw className="w-4 h-4 mr-1" /> Retry</>
                          )}
                        </Button>
                      )}
                      
                      {/* Freeze/Unfreeze */}
                      {payout.payout_frozen ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUnfreeze(payout.user_id)}
                          className="border-emerald-500/50 text-emerald-400"
                        >
                          <Unlock className="w-4 h-4 mr-1" /> Unfreeze
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const reason = prompt('Enter reason for freezing payouts:');
                            if (reason) handleFreeze(payout.user_id, reason);
                          }}
                          className="border-red-500/50 text-red-400"
                        >
                          <Ban className="w-4 h-4 mr-1" /> Freeze
                        </Button>
                      )}
                      
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setShowAuditModal(true); fetchAuditLog(payout.id); }}
                        className="text-muted-foreground"
                      >
                        <Eye className="w-4 h-4 mr-1" /> History
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">Reject Payout Request</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              This will reject the payout and restore the amount to the user's wallet balance.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground mb-2">Amount: <span className="text-emerald-400 font-bold">{formatCurrency(selectedPayout?.amount)}</span></p>
              <p className="text-sm text-muted-foreground">Creator: <span className="text-foreground">{selectedPayout?.creator_name}</span></p>
            </div>
            <div>
              <label className="text-sm text-muted-foreground block mb-2">Rejection Reason (required)</label>
              <Textarea
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                className="bg-secondary border-border"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectModal(false)} className="border-border">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={!actionReason.trim() || actionLoading[selectedPayout?.id]}
            >
              {actionLoading[selectedPayout?.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reject & Restore Balance'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fail Modal */}
      <Dialog open={showFailModal} onOpenChange={setShowFailModal}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">Mark Payout as Failed</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              This will mark the payout as failed and restore the amount to the user's wallet balance.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground mb-2">Amount: <span className="text-emerald-400 font-bold">{formatCurrency(selectedPayout?.amount)}</span></p>
              <p className="text-sm text-muted-foreground">Creator: <span className="text-foreground">{selectedPayout?.creator_name}</span></p>
            </div>
            <div>
              <label className="text-sm text-muted-foreground block mb-2">Failure Reason (required)</label>
              <Textarea
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                placeholder="Enter reason for failure (e.g., Bank rejected transfer)..."
                className="bg-secondary border-border"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFailModal(false)} className="border-border">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleFail}
              disabled={!actionReason.trim() || actionLoading[selectedPayout?.id]}
            >
              {actionLoading[selectedPayout?.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Mark Failed & Restore Balance'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete Modal */}
      <Dialog open={showCompleteModal} onOpenChange={setShowCompleteModal}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">Complete Payout</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Confirm that the bank transfer has been completed successfully.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground mb-2">Amount: <span className="text-emerald-400 font-bold">{formatCurrency(selectedPayout?.amount)}</span></p>
              <p className="text-sm text-muted-foreground">Creator: <span className="text-foreground">{selectedPayout?.creator_name}</span></p>
              <p className="text-sm text-muted-foreground">Bank: <span className="text-foreground">{selectedPayout?.bank_name} ****{selectedPayout?.account_last4}</span></p>
            </div>
            <div>
              <label className="text-sm text-muted-foreground block mb-2">Payout Reference (optional)</label>
              <Input
                value={payoutReference}
                onChange={(e) => setPayoutReference(e.target.value)}
                placeholder="Enter bank transfer reference ID..."
                className="bg-secondary border-border"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompleteModal(false)} className="border-border">
              Cancel
            </Button>
            <Button
              onClick={handleComplete}
              disabled={actionLoading[selectedPayout?.id]}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              {actionLoading[selectedPayout?.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Mark as Completed'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit Log Modal */}
      <Dialog open={showAuditModal} onOpenChange={setShowAuditModal}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground">Payout Audit Log</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              All admin actions on payout requests
            </DialogDescription>
          </DialogHeader>
          {auditLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
            </div>
          ) : auditLogs.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No audit logs found</p>
          ) : (
            <div className="space-y-3">
              {auditLogs.map((log, i) => (
                <div key={i} className="p-3 bg-secondary/50 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <Badge className={
                      log.action === 'approve' ? 'bg-emerald-500/20 text-emerald-400' :
                      log.action === 'reject' || log.action === 'fail' ? 'bg-red-500/20 text-red-400' :
                      log.action === 'complete' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-slate-500/20 text-muted-foreground'
                    }>
                      {log.action}
                    </Badge>
                    <span className="text-muted-foreground text-xs">{formatDate(log.timestamp)}</span>
                  </div>
                  <p className="text-foreground text-sm">Admin: {log.admin_name}</p>
                  {log.details?.reason && (
                    <p className="text-muted-foreground text-xs mt-1">Reason: {log.details.reason}</p>
                  )}
                  {log.details?.amount && (
                    <p className="text-emerald-400 text-xs">Amount: {formatCurrency(log.details.amount)}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPayoutManagement;
